import { animate, keyframes, style,transition, trigger } from "@angular/animations";
import { CommonModule, DOCUMENT } from "@angular/common";
import {
  AfterViewInit,
  ChangeDetectionStrategy, Component, EventEmitter,
  HostListener, Inject, Input, OnChanges, Output, signal, SimpleChanges } from "@angular/core";
import { MatBadgeModule } from "@angular/material/badge";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatMenuModule } from "@angular/material/menu";
import { Router } from "@angular/router";
import { filter, takeUntil } from "rxjs";

import { LanguageSwitcherComponent } from "@shared/components/language-switcher/language-switcher.component";

import { openConfirmDialog } from "../confirmation-dialog/confirmation-dialog.constant";

import { ELEMENT_ID } from "@app/core/constants/element-ids.const";
import { NAVBAR_ITEM } from "@app/core/constants/navigation.const";
import { PATHS } from "@app/core/constants/path.const";
import { AuthService } from "@app/core/services/auth.service";
import { DestroyService } from "@app/core/services/destroy.service";
import { SvgIconsService } from "@app/core/services/svg-icons.service";
import { GlobalSearchComponent } from "@app/features/global-search/global-search.component";
import { TNavigation } from "@app/shared/components/under-ui-header/types/navbar-item.type";
import { ThemeService } from "@app/shared/services/app-theme.service";
import { TranslatePipe } from "@ngx-translate/core";

export const POLICY_HEADER_ICONS = {
  bell: "bell",
  user: "user",
  navbarRight: "navbar-right",
  sidebarCollapse: "sidebar-collapse",
  sidebarExpand: "sidebar-expand",
  workspace: "workspace",
  dashboard: "dashboard",
  tasks: "tasks",
  catastropheEvents: "catastrophe-events",
  productsChild: "products-child",
  adminProducts: "admin-products",
  ratingTable: "rating-table",
  coverageTypes: "coverage-types",
  attributes: "attributes",
  billing: "billing",
  invoices: "invoices",
  generalLedger: "general-ledger",
  captiveManagement: "organizations",
  accounts: "accounts",
  books: "books",
  departments: "departments",
  postingEngine: "posting-engine",
  journalEntries: "journal-entries",
  recurringJournals: "recurring-journals",
  trialBalance: "trial-balance",
  subledgerReconciliation: "subledger-reconciliation",
  bankReconciliation: "bank-reconciliation",
  cashTransactions: "cash-transactions",
  accountsReceivable: "accounts-receivable",
  accountsPayable: "accounts-payable",
  arCustomers: "ar-customers",
  vendors: "vendors",
  arAging: "ar-aging",
  apAging: "ap-aging",
  settings: "settings",
  users: "users",
  roles: "roles",
  notifications: "notifications",
  emailLogs: "email-logs",
  territoriesAndTaxes: "territories-and-taxes",
  endorsements: "endorsements",
  organizations: "organizations",
  documentTemplateCenter: "document-template-center",
  tagsLibrary: "tags-library",
  riskTypes: "risk-types",
  lossRunTemplates: "loss-run-templates",
  workflows: "workflows",
  lossRunIntelligence: "loss-run-intelligence",
  lossRun: "loss-run",
  balanceSheet: "balance-sheet",
  incomeStatement: "income-statement",
  expenseStatement: "expense-statement",
  cashFlow: "cash-flow",
  glDetail: "gl-detail",
};

@Component({
  selector: "under-ui-header",
  templateUrl: "./under-ui-header.component.html",
  styleUrl: "./under-ui-header.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger("bellShake", [
      transition("false => true", [
        animate(
          "800ms ease-in-out",
          keyframes([
            style({ transform: "rotate(0deg)", offset: 0 }),
            style({ transform: "rotate(15deg)", offset: 0.1 }),
            style({ transform: "rotate(-15deg)", offset: 0.2 }),
            style({ transform: "rotate(10deg)", offset: 0.3 }),
            style({ transform: "rotate(-10deg)", offset: 0.4 }),
            style({ transform: "rotate(5deg)", offset: 0.5 }),
            style({ transform: "rotate(-5deg)", offset: 0.6 }),
            style({ transform: "rotate(2deg)", offset: 0.7 }),
            style({ transform: "rotate(0deg)", offset: 1 }),
          ]),
        ),
      ]),
    ]),
  ],
  imports: [
    CommonModule,
    MatIconModule,
    MatMenuModule,
    MatButtonModule,
    TranslatePipe,
    MatBadgeModule,
    GlobalSearchComponent,
    LanguageSwitcherComponent,
  ],
  standalone: true,
})
export class UnderUiHeaderComponent implements OnChanges, AfterViewInit {
  @Input({ required: true }) unreadNotificationsCount!: number;
  @Input() isSidebarCollapsed = false;

  @Output() toggleNotificationSidebar = new EventEmitter<void>();
  @Output() toggleSidebar = new EventEmitter<void>();

  @HostListener("document:fullscreenchange")
  onFullScreenChange(): void {
    this.isFullScreen = !!this.document.fullscreenElement;
  }

  onAnimationRun = signal<boolean>(false);

  navbarItems: TNavigation[] = NAVBAR_ITEM;
  protected readonly elementId = ELEMENT_ID;
  protected isFullScreen = false;
  protected isAvailableFullScreen = false;

  private readonly bellSounds = new Audio("bell.wav");

  constructor(
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly svgIconsService: SvgIconsService,
    private readonly themeService: ThemeService,
    private readonly matDialog: MatDialog,
    private readonly destroy$: DestroyService,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {
    const icons = [
      ...this.navbarItems.map(x => x.icon || ""),
      ...Object.values(POLICY_HEADER_ICONS),
      "cancel",
    ];
    this.svgIconsService.registerIcons(icons);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["unreadNotificationsCount"] && !changes["unreadNotificationsCount"].firstChange) {
      this.onAnimationRun.set(true);

      // temporary disabled
      if (this.bellSounds && false) {
        this.onRunBellSound();
      }
    }
  }

  ngAfterViewInit(): void {
    this.isAvailableFullScreen = this.document.fullscreenEnabled;

    this.isFullScreen = !!this.document.fullscreenElement;
  }

  logout(): void {
    openConfirmDialog(this.matDialog, {
      title: "CANCEL_DIALOG.CONFIRMATION_TITLE",
      message: "COMMON.LOGOUT_CONFIRM_MESSAGE",
      confirmText: "BTN.CONFIRM",
      cancelText: "BTN.CANCEL",
    })
      .pipe(
        filter((confirmed: boolean) => confirmed),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.authService.clearAuthorizationData();
        this.router.navigate([PATHS.auth.path, PATHS.auth.login.path]);
      });
  }

  openProfile(): void {
    this.router.navigate([PATHS.profile.path]);
  }

  switchTheme() {
    let theme = this.themeService.getTheme();
    theme = theme === "light" ? "dark" : "light";
    this.themeService.setTheme(theme);
  }

  toggleNotificationsSidebar(): void {
    this.toggleNotificationSidebar.emit();
  }

  getThemeSwitchLabel(): string {
    const currentTheme = this.themeService.getTheme();

    return currentTheme === "light" ? "COMMON.SWITCH_TO_DARK_MODE" : "COMMON.SWITCH_TO_LIGHT_MODE";
  }

  onAnimationEnd(): void {
    this.onAnimationRun.set(false);
  }

  protected fullScreen(): void {
    if (this.isAvailableFullScreen) {
      this.document.fullscreenElement
        ? this.document.exitFullscreen()
        : this.document.documentElement.requestFullscreen();
    }
  }

  private onRunBellSound(): void {
    this.bellSounds.currentTime = 0;

    this.bellSounds.play().catch(err => console.log(err));
  }
}
